/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Form, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import MemoizedLLMFieldInput from './LLMFieldInput';

// Misc imports
import { useFieldVisibilityValueManager } from '../../../../utils/hooks/useLLMFieldVisibilityValueManager';
import { INPUT_BASE_TYPES, INPUT_ELEMENT_TYPES } from '../../../../constants/llmConfiguration';

// Used to check if a field should be hidden based on the target value
const checkFieldVisibility = (mapping, actualValue) => {
  if (!mapping.visible) {
    return true;
  }

  const { comparator, value: targetValue } = mapping.visible;

  switch (comparator) {
    case 'greater': return actualValue > targetValue;
    case 'equal': return actualValue === targetValue;
    case 'less': return actualValue < targetValue;
    default: return true;
  }
};

const SingleConfigField = ({ field, basePath, form }) => {
  // Field name is created using the base path and the association.key of the mappings, reference to the RFC
  const fieldName = useMemo(() => [...basePath, field.association.key], [basePath, field]);

  const isRequired = field?.optional === false;

  /*************************************************************/
  // Create the dependency path to the element that is needed to subscribe
  // and watch for deciding if the field should be hidden or not
  const dependencyPath = useMemo(() => {
    if (!field.visible) {
      return null;
    }
    return Array.isArray(basePath)
      ? [...basePath, field.visible.otherElementId]
      : [basePath, field.visible.otherElementId];
  }, [basePath, field.visible]);

  // Subscribe to the value the field depends on
  const dependencyValue = Form.useWatch(dependencyPath, form);

  const isVisible = useMemo(() => {
    return checkFieldVisibility(field, dependencyValue);
  }, [field, dependencyValue]);
  /*************************************************************/

  // Used to handle the values when a field is alternating between being visible
  // and hidden
  useFieldVisibilityValueManager(isVisible, fieldName, form, field.defaultValue);

  if (!isVisible) {
    return null;
  }

  const isBooleanInput = field.type === INPUT_BASE_TYPES.BOOLEAN || field.elementType === INPUT_ELEMENT_TYPES.SWITCH;

  return (
    <Form.Item
      name={fieldName}
      key={field.id}
      labelAlign="left"
      required={isRequired}
      colon={false}
      className="model-configuration-form-group mb-4"
      valuePropName={isBooleanInput ? 'checked' : 'value'}
      label={field.label}
      tooltip={
        field.description
          ? {
            icon: <InfoCircleOutlined />,
            title: <Typography.Text type="secondary">{field.description}</Typography.Text>
          }
          : undefined
      }
      rules={[{ required: isRequired, message: t('Field cannot be blank.') }]}
    >
      <MemoizedLLMFieldInput field={field}/>
    </Form.Item>
  );
};

SingleConfigField.propTypes = {
  field: PropTypes.object.isRequired,
  basePath: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired
};

const MemoizedSingleConfigField = React.memo(SingleConfigField);

export default MemoizedSingleConfigField;
